/**
 * GPX Parser Utility
 * Parses GPX data to extract elevation profile information
 */

/**
 * Parse GPX data and extract elevation points and waypoints
 * @param {string} gpxData - GPX data as XML string
 * @returns {Object} Object with elevationPoints array, pointsOfInterest array, and routeName
 */
export function parseGPXElevation(gpxData) {
    try {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(gpxData, 'text/xml');

        // Check for parsing errors
        const parseError = xmlDoc.querySelector('parsererror');
        if (parseError) {
            throw new Error('Invalid GPX data format');
        }

        // Extract route name from GPX metadata (works for both GPX 1.0 and 1.1)
        const metadataName = xmlDoc.querySelector('metadata > name');
        let routeName = metadataName ? metadataName.textContent.trim() : null;

        // Support both tracks (trkpt) and routes (rtept) - works for GPX 1.0 and 1.1
        const trackPoints = xmlDoc.querySelectorAll('trkpt');
        const routePoints = xmlDoc.querySelectorAll('rtept');

        // Use track points if available, otherwise use route points
        const points = trackPoints.length > 0 ? trackPoints : routePoints;

        if (points.length === 0) {
            throw new Error('No track or route points found in GPX data');
        }

        // If no metadata name, try to get route name from route element
        if (!routeName && routePoints.length > 0) {
            const routeElement = xmlDoc.querySelector('rte');
            if (routeElement) {
                const routeNameElement = routeElement.querySelector('name');
                if (routeNameElement) {
                    routeName = routeNameElement.textContent.trim();
                }
            }
        }

        // If still no name, try to get track name
        if (!routeName && trackPoints.length > 0) {
            const trackElement = xmlDoc.querySelector('trk');
            if (trackElement) {
                const trackNameElement = trackElement.querySelector('name');
                if (trackNameElement) {
                    routeName = trackNameElement.textContent.trim();
                }
            }
        }

        const elevationPoints = [];
        let cumulativeDistance = 0;
        let previousLat = null;
        let previousLng = null;

        points.forEach((point, index) => {
            const lat = parseFloat(point.getAttribute('lat'));
            const lng = parseFloat(point.getAttribute('lon'));
            const elevationElement = point.querySelector('ele');

            if (elevationElement) {
                const elevation = parseFloat(elevationElement.textContent);

                // Calculate distance from previous point
                if (previousLat !== null && previousLng !== null) {
                    const distance = calculateDistance(previousLat, previousLng, lat, lng);
                    cumulativeDistance += distance;
                }

                elevationPoints.push({
                    distance: cumulativeDistance, // Distance in meters
                    elevation: elevation, // Elevation in meters
                    lat: lat,
                    lng: lng,
                    index: index
                });

                previousLat = lat;
                previousLng = lng;
            }
        });

        if (elevationPoints.length === 0) {
            throw new Error('No elevation data found in GPX');
        }

        // Parse waypoints (POIs)
        const waypoints = xmlDoc.querySelectorAll('wpt');
        const pointsOfInterest = Array.from(waypoints).map(wpt => {
            const lat = parseFloat(wpt.getAttribute('lat'));
            const lng = parseFloat(wpt.getAttribute('lon'));
            const nameElement = wpt.querySelector('name');
            const descElement = wpt.querySelector('desc');
            const typeElement = wpt.querySelector('type');
            const symElement = wpt.querySelector('sym');

            // Match RWGPS format: { lat, lng, n (name), t (type), d (description) }
            return {
                lat: lat,
                lng: lng,
                n: nameElement ? nameElement.textContent.trim() : null, // name
                t: typeElement ? typeElement.textContent.trim() : (symElement ? symElement.textContent.trim() : null), // type, fallback to symbol
                d: descElement ? descElement.textContent.trim() : null // description
            };
        });

        return {
            elevationPoints,
            pointsOfInterest,
            routeName
        };
    } catch (error) {
        console.error('Error parsing GPX elevation data:', error);
        throw error;
    }
}

/**
 * Calculate distance between two lat/lng points using Haversine formula
 * @param {number} lat1 - First point latitude
 * @param {number} lng1 - First point longitude
 * @param {number} lat2 - Second point latitude
 * @param {number} lng2 - Second point longitude
 * @returns {number} Distance in meters
 */
function calculateDistance(lat1, lng1, lat2, lng2) {
    const R = 6371000; // Earth's radius in meters
    const dLat = toRadians(lat2 - lat1);
    const dLng = toRadians(lng2 - lng1);

    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
}

/**
 * Convert degrees to radians
 * @param {number} degrees - Degrees to convert
 * @returns {number} Radians
 */
function toRadians(degrees) {
    return degrees * (Math.PI / 180);
}

/**
 * Convert GPX elevation points to chart data format
 * @param {Array} elevationPoints - Array of elevation points from parseGPXElevation
 * @param {Object} chartSettings - Chart settings (not used for conversion, chunkCharts handles multipliers)
 * @returns {Array} Array of point objects in the format expected by chunkCharts
 */
export function convertGPXToChartData(elevationPoints, chartSettings) {
    return elevationPoints.map(point => ({
        d: point.distance, // Distance in meters (chunkCharts will apply distMultiplier)
        e: point.elevation, // Elevation in meters (chunkCharts will apply elevMultiplier)
        n: null, // No names for GPX points
        x: point.lng, // Longitude
        y: point.lat // Latitude
    }));
}

/**
 * Sample GPX elevation points to reduce data density if needed
 * @param {Array} elevationPoints - Full array of elevation points from parseGPXElevation
 * @param {number} maxPoints - Maximum number of points to return
 * @returns {Array} Sampled elevation points
 */
export function sampleGPXPoints(elevationPoints, maxPoints = 1000) {
    if (elevationPoints.length <= maxPoints) {
        return elevationPoints;
    }

    const step = Math.floor(elevationPoints.length / maxPoints);
    const sampledPoints = [];

    for (let i = 0; i < elevationPoints.length; i += step) {
        sampledPoints.push(elevationPoints[i]);
    }

    // Always include the last point
    if (sampledPoints[sampledPoints.length - 1] !== elevationPoints[elevationPoints.length - 1]) {
        sampledPoints.push(elevationPoints[elevationPoints.length - 1]);
    }

    return sampledPoints;
}
